import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  FileText,
  ScanLine,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../components/Loading";
import { PageHeader } from "../components/ui";
import { api } from "../lib/api";
import type { DependentVerification } from "../lib/api";
import { useLegalEntityGate } from "../lib/legalEntity";

const REVIEW_TABS = ["pending", "auto_matched", "approved", "rejected"] as const;

function OCRConfidence({ value }: { value: number | null }) {
  if (value === null) return <span className="text-text-tertiary">—</span>;
  const pct = Math.round(value * 100);
  const tone =
    pct >= 85 ? "emerald" : pct >= 60 ? "amber" : "rose";
  const cls =
    tone === "emerald"
      ? "bg-emerald-100 text-emerald-800"
      : tone === "amber"
      ? "bg-amber-100 text-amber-800"
      : "bg-rose-100 text-rose-800";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${cls}`}
    >
      {pct}% conf.
    </span>
  );
}

function VerificationCard({
  ver,
  onApprove,
  onReject,
}: {
  ver: DependentVerification;
  onApprove: () => void;
  onReject: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border border-border-subtle bg-white p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-text-secondary" />
          <div>
            <div className="text-sm font-medium text-text-primary">
              {ver.extracted_full_name ?? "(no name extracted)"}
            </div>
            <div className="text-xs text-text-tertiary">
              {ver.document_kind_declared.replace(/_/g, " ")} ·{" "}
              {ver.employee_id.slice(0, 8)}…
            </div>
          </div>
        </div>
        <OCRConfidence value={ver.ocr_confidence} />
      </div>
      <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-text-secondary">
        <div>
          <dt className="text-text-tertiary">{t("benefits.verif_extracted_kind", "Extracted kind")}</dt>
          <dd>{ver.extracted_document_kind ?? "—"}</dd>
        </div>
        <div>
          <dt className="text-text-tertiary">{t("benefits.verif_event_date", "Event date")}</dt>
          <dd>{ver.extracted_date_of_event ?? "—"}</dd>
        </div>
        <div>
          <dt className="text-text-tertiary">{t("benefits.verif_jurisdiction", "Jurisdiction")}</dt>
          <dd>{ver.extracted_issuance_jurisdiction ?? "—"}</dd>
        </div>
        <div>
          <dt className="text-text-tertiary">{t("benefits.verif_ocr_status", "OCR status")}</dt>
          <dd>{ver.ocr_status.replace(/_/g, " ")}</dd>
        </div>
        {ver.review_notes && (
          <div className="col-span-2">
            <dt className="text-text-tertiary">{t("benefits.verif_notes", "Review notes")}</dt>
            <dd className="text-text-primary">{ver.review_notes}</dd>
          </div>
        )}
        <div className="col-span-2">
          <dt className="text-text-tertiary">{t("benefits.verif_doc_url", "Document")}</dt>
          <dd>
            <a
              href={ver.document_url}
              target="_blank"
              rel="noreferrer"
              className="text-brand-deep underline"
            >
              {ver.document_url.slice(0, 60)}…
            </a>
          </dd>
        </div>
      </dl>
      {(ver.review_status === "pending" || ver.review_status === "auto_matched") && (
        <div className="mt-3 flex gap-1.5">
          <button
            type="button"
            onClick={onApprove}
            className="rounded-lg bg-emerald-700 px-2 py-1 text-[11px] font-medium text-white hover:opacity-95"
          >
            <CheckCircle2 className="mr-1 inline h-3 w-3" />
            {t("benefits.verif_approve", "Approve")}
          </button>
          <button
            type="button"
            onClick={onReject}
            className="rounded-lg border border-rose-300 px-2 py-1 text-[11px] font-medium text-rose-700 hover:bg-rose-50"
          >
            <XCircle className="mr-1 inline h-3 w-3" />
            {t("benefits.verif_reject", "Reject")}
          </button>
        </div>
      )}
    </div>
  );
}

function SubmitDialog({
  legalEntityId,
  onClose,
  onSubmitted,
}: {
  legalEntityId: string;
  onClose: () => void;
  onSubmitted: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    employee_id: "",
    document_kind_declared: "birth_certificate",
    document_url: "",
  });
  const [error, setError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: () =>
      api.submitDependentVerification({
        legal_entity_id: legalEntityId,
        ...form,
      }),
    onSuccess: () => {
      onSubmitted();
      onClose();
    },
    onError: (e: unknown) => {
      setError(e instanceof Error ? e.message : String(e));
    },
  });
  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30">
      <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-xl">
        <h3 className="text-base font-semibold text-text-primary">
          {t("benefits.verif_submit_title", "Submit certificate for OCR")}
        </h3>
        <p className="mt-1 text-xs text-text-tertiary">
          {t(
            "benefits.verif_submit_help",
            "Sonnet classifies + extracts; HR Director reviews unmatched cases.",
          )}
        </p>
        <div className="mt-3 space-y-3">
          <div>
            <label className="block text-xs font-medium text-text-secondary">
              {t("benefits.verif_employee_id", "Employee ID (UUID)")}
            </label>
            <input
              value={form.employee_id}
              onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
              className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 font-mono text-xs"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary">
              {t("benefits.verif_kind", "Document kind")}
            </label>
            <select
              value={form.document_kind_declared}
              onChange={(e) =>
                setForm({ ...form, document_kind_declared: e.target.value })
              }
              className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2"
            >
              {[
                "birth_certificate",
                "marriage_certificate",
                "death_certificate",
                "education_certificate",
                "other",
              ].map((k) => (
                <option key={k} value={k}>
                  {k.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary">
              {t("benefits.verif_doc_url_label", "Document URL")}
            </label>
            <input
              value={form.document_url}
              onChange={(e) => setForm({ ...form, document_url: e.target.value })}
              placeholder="https://r2.solvait/.../cert.pdf"
              className="mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 text-xs"
            />
          </div>
          {error && (
            <div className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-800">
              {error}
            </div>
          )}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border-subtle px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-sunken"
          >
            {t("common.cancel", "Cancel")}
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null);
              mutation.mutate();
            }}
            disabled={!form.employee_id || !form.document_url}
            className="rounded-lg bg-brand-deep px-3 py-1.5 text-sm font-medium text-white hover:opacity-95 disabled:opacity-50"
          >
            {t("benefits.verif_submit", "Submit")}
          </button>
        </div>
      </div>
    </div>
  );
}

export function DependentVerificationsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [tab, setTab] = useState<(typeof REVIEW_TABS)[number]>("pending");
  const [submitOpen, setSubmitOpen] = useState(false);

  const list = useQuery({
    queryKey: ["dependent-verifications", legalEntityId, tab],
    queryFn: () =>
      api.listDependentVerifications(legalEntityId ?? "", {
        reviewStatus: tab,
      }),
    enabled: !!legalEntityId,
  });

  const review = useMutation({
    mutationFn: ({
      id,
      approved,
      notes,
    }: {
      id: string;
      approved: boolean;
      notes?: string;
    }) =>
      api.reviewDependentVerification(id, approved, {
        reviewer_clerk_user_id: "hr_director",
        notes,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dependent-verifications"] });
    },
  });

  if (gate) return gate;

  return (
    <div className="space-y-4">
      <PageHeader
        title={t(
          "benefits.verif_title",
          "Dependent verifications (Sonnet OCR)",
        )}
        subtitle={t(
          "benefits.verif_subtitle",
          "Birth / marriage / death / education certificates. AI OCR classifies + extracts; HR Director reviews unmatched cases.",
        )}
      />
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setSubmitOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand-deep px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
        >
          <ScanLine className="h-4 w-4" />
          {t("benefits.verif_new", "Submit new certificate")}
        </button>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-border-subtle">
        {REVIEW_TABS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setTab(s)}
            className={`-mb-px rounded-t-lg border-b-2 px-3 py-1 text-xs capitalize ${
              tab === s
                ? "border-brand-blue text-brand-deep"
                : "border-transparent text-text-secondary hover:text-text-primary"
            }`}
          >
            {s.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-amber-300 bg-amber-50/60 p-3 text-xs text-amber-900 flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <div>
          {t(
            "benefits.verif_pdpl_note",
            "Health declarations + identity certificates are PDPL-sensitive. Storage is KSA-resident; access is HR-Director-only with audit-logged review.",
          )}
        </div>
      </div>

      {list.isLoading && <Loading />}
      {list.isError && (
        <ErrorPanel message={(list.error as Error).message} />
      )}
      {list.data && list.data.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border-subtle p-6 text-center text-sm text-text-tertiary">
          {t(
            "benefits.verif_empty",
            "No verifications in this status.",
          )}
        </div>
      )}
      {list.data && list.data.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {list.data.map((ver) => (
            <VerificationCard
              key={ver.id}
              ver={ver}
              onApprove={() =>
                review.mutate({ id: ver.id, approved: true })
              }
              onReject={() => {
                const reason = window.prompt(
                  t(
                    "benefits.verif_reject_reason_prompt",
                    "Rejection reason (audit-logged):",
                  ),
                );
                if (reason && reason.trim().length > 0) {
                  review.mutate({
                    id: ver.id,
                    approved: false,
                    notes: reason,
                  });
                }
              }}
            />
          ))}
        </div>
      )}

      {submitOpen && legalEntityId && (
        <SubmitDialog
          legalEntityId={legalEntityId}
          onClose={() => setSubmitOpen(false)}
          onSubmitted={() =>
            qc.invalidateQueries({ queryKey: ["dependent-verifications"] })
          }
        />
      )}
    </div>
  );
}
